import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useOpenAuthedConcierge } from "@/hooks/useOpenAuthedConcierge";
import { useFloatingConciergeUi } from "@/contexts/FloatingConciergeContext";
import type { TeamClientActionHandlers } from "@/lib/teamClientActions";
import { format } from "date-fns";
import { Plus, Upload, Search, AlertTriangle, Sparkles, ChevronRight } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/lembra/PageHeader";
import { PageShell } from "@/components/lembra/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddTeamMemberDialog, TeamMemberFormData } from "@/components/lembra/AddTeamMemberDialog";
import {
  ImportCsvDialog,
  type ImportAnalysis,
  type ExistingMemberLite,
} from "@/components/lembra/ImportCsvDialog";
import { toast } from "sonner";
import { ErrorCard } from "@/components/lembra/ErrorCard";
import { PageMeta } from "@/components/lembra/PageMeta";
import { useProfile, useTeamMembers, type TeamMemberRow } from "@/hooks/queries";
import {
  getMissingFields,
  fieldLabel,
  summariseCompleteness,
} from "@/lib/teamMember";

export default function Team() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { openFromChatUrl } = useOpenAuthedConcierge();
  const { registerTeamClientActions } = useFloatingConciergeUi();
  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;

  const membersQuery = useTeamMembers(companyId);
  // MS139: same useMemo perf pattern as MS136-138. The `?? []` fallback
  // created a fresh array reference every render → the 3 dependent
  // useMemo hooks below (filtered / completeness / existingLite-ish)
  // re-ran every render. Team is one of the most-visited customer
  // surfaces (admin loads it on first dashboard visit).
  const members = useMemo(() => membersQuery.data ?? [], [membersQuery.data]);
  const loading =
    profileQuery.isLoading ||
    (Boolean(companyId) && membersQuery.isLoading);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  // MS392: default sort flipped "name" → "birthday" (next upcoming
  // birthday). Audit feedback: "Name A–Z" is the right OPTION but the
  // wrong DEFAULT for a celebrations dashboard. The dominant use case
  // when the buyer opens /team is "who's coming up next", not "find a
  // specific person by name." Buyers searching by name use the search
  // box; the sort dropdown is for browsing. Default browse order should
  // surface the people the buyer is about to make decisions for.
  const [sort, setSort] = useState("birthday");
  const [addOpen, setAddOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [editMember, setEditMember] = useState<TeamMemberRow | null>(null);
  const [highlightedMemberId, setHighlightedMemberId] = useState<string | null>(null);

  const teamClientActionHandlers = useMemo<TeamClientActionHandlers>(
    () => ({
      highlightTeamMember: (teamMemberId: string) => {
        setHighlightedMemberId(teamMemberId);
        window.setTimeout(() => {
          setHighlightedMemberId((current) =>
            current === teamMemberId ? null : current,
          );
        }, 4000);
      },
      openTeamMemberProfile: (teamMemberId: string) => {
        navigate(`/team/${teamMemberId}/profile`);
      },
      openAddTeamMember: () => {
        setEditMember(null);
        setAddOpen(true);
      },
    }),
    [navigate],
  );

  useEffect(() => {
    registerTeamClientActions(teamClientActionHandlers);
    return () => registerTeamClientActions(null);
  }, [teamClientActionHandlers, registerTeamClientActions]);

  // Deep-link: /team?add=1 auto-opens the Add Team Member dialog. Used
  // by the onboarding checklist's "Add your team" tile so the user
  // isn't dropped at a page wondering which button to hit. We strip
  // the param after handling so a refresh doesn't re-open the dialog
  // and the URL stays clean.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setEditMember(null);
      setAddOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
    // Intentionally only on mount / when the param appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("concierge") !== "1") return;
    const memberId = searchParams.get("member");
    const member = memberId ? members.find((m) => m.id === memberId) : null;
    const brief = member
      ? `Help me update ${member.name}'s team profile: hobbies, dietary needs, and gift preferences.`
      : "Help me update team profiles: birthdays, dietary needs, and gift preferences.";
    openFromChatUrl(`/chat?brief=${encodeURIComponent(brief)}`);
    const next = new URLSearchParams(searchParams);
    next.delete("concierge");
    next.delete("member");
    setSearchParams(next, { replace: true });
  }, [searchParams, members, openFromChatUrl, setSearchParams]);

  // Any mutation against team_members should invalidate both the full list
  // and the dashboard's active-count query. Invalidating on the shared
  // prefix ["team_members", companyId] catches both with one call.
  const invalidateTeam = () => {
    if (!companyId) return;
    queryClient.invalidateQueries({ queryKey: ["team_members", companyId] });
  };

  const handleSave = async (data: TeamMemberFormData) => {
    if (!companyId) return;
    const enrichmentPayload = {
      hobbies: data.hobbies,
      favourite_foods: data.favouriteFoods,
      personal_notes: data.personalNotes.trim() || null,
      gift_preferences:
        data.giftPreferences &&
        (
          (data.giftPreferences.vibes ?? []).length > 0 ||
          (data.giftPreferences.avoid ?? []).length > 0 ||
          (data.giftPreferences.desired_categories ?? []).length > 0 ||
          (data.giftPreferences.notes?.trim().length ?? 0) > 0
        )
          ? {
              vibes: data.giftPreferences.vibes ?? [],
              avoid: data.giftPreferences.avoid ?? [],
              desired_categories: data.giftPreferences.desired_categories ?? [],
              notes: data.giftPreferences.notes?.trim() || undefined,
            }
          : null,
    };

    if (editMember) {
      const { error } = await supabase
        .from("team_members")
        .update({
          name: data.name,
          email: data.email,
          // MS438: birthday/startDate are optional (admin-seeded rows may omit them).
          // The ! assertion suppresses TypeScript but format(undefined, ...) throws at runtime.
          birthday: data.birthday ? format(data.birthday, "yyyy-MM-dd") : null,
          start_date: data.startDate ? format(data.startDate, "yyyy-MM-dd") : null,
          dietary_requirements: data.dietaryRequirements,
          allergies: data.allergies,
          // MS390 Bug #8: persist explicit "confirmed" markers so
          // the /team needs-attention banner can distinguish
          // unknown-empty from confirmed-empty.
          dietary_confirmed: data.dietaryConfirmed,
          allergies_confirmed: data.allergiesConfirmed,
          department: data.department || null,
          office_location: data.officeLocation || null,
          ...enrichmentPayload,
          enriched_at: new Date().toISOString(),
        })
        .eq("id", editMember.id);
      if (error) { toast.error("Failed to update member"); return; }
      toast.success("Member updated");
    } else {
      const hasEnrichment =
        data.hobbies.length > 0 ||
        data.favouriteFoods.length > 0 ||
        data.personalNotes.trim().length > 0 ||
        enrichmentPayload.gift_preferences != null;
      const { data: inserted, error } = await supabase
        .from("team_members")
        .insert({
          company_id: companyId,
          name: data.name,
          email: data.email,
          birthday: data.birthday ? format(data.birthday, "yyyy-MM-dd") : null,
          start_date: data.startDate ? format(data.startDate, "yyyy-MM-dd") : null,
          dietary_requirements: data.dietaryRequirements,
          allergies: data.allergies,
          dietary_confirmed: data.dietaryConfirmed,
          allergies_confirmed: data.allergiesConfirmed,
          department: data.department || null,
          office_location: data.officeLocation || null,
          ...enrichmentPayload,
          ...(hasEnrichment ? { enriched_at: new Date().toISOString() } : {}),
        })
        .select("id")
        .single();
      if (error) { toast.error("Failed to add member"); return; }
      toast.success("Member added", {
        description: "Add gift context so Lembra can recommend the perfect celebration.",
      });
      setAddOpen(false);
      setEditMember(null);
      invalidateTeam();
      navigate(`/team/${inserted.id}/profile?enrich=1`);
      return;
    }
    setAddOpen(false);
    setEditMember(null);
    invalidateTeam();
  };

  const handleDeactivate = async (id: string) => {
    const member = members.find((m) => m.id === id);
    if (!member) return;
    const { error } = await supabase
      .from("team_members")
      .update({ is_active: !member.is_active })
      .eq("id", id);
    if (error) { toast.error("Failed to update status"); return; }
    toast.success(member.is_active ? "Member deactivated" : "Member reactivated");
    setAddOpen(false);
    setEditMember(null);
    invalidateTeam();
  };

  // Project the live team list down to the lite shape the dialog
  // needs for dedup (id + matchable fields). Recomputed only when
  // the underlying members change, so it's cheap.
  const existingMembersLite = useMemo<ExistingMemberLite[]>(() => {
    return members.map((m) => ({
      id: m.id,
      name: m.name,
      email: m.email,
      birthday: m.birthday ?? null,
      start_date: m.start_date ?? null,
      department: m.department ?? null,
      office_location: m.office_location ?? null,
      dietary_requirements: m.dietary_requirements ?? null,
      allergies: m.allergies ?? null,
      data_source: m.data_source ?? null,
    }));
  }, [members]);

  const handleCsvImport = async (analysis: ImportAnalysis) => {
    if (!companyId) return;

    // Inserts are straightforward: every CSV row that didn't match an
    // existing member becomes a new team_members row tagged as
    // user_csv. Birthday / start_date are nullable now, so empty
    // strings collapse to NULL rather than "today" (the prior hack).
    // Allergies are kept distinct from dietary_requirements per the
    // 20260430030000 migration, never merged together.
    const inserts = analysis.toInsert.map((r) => ({
      company_id: companyId,
      name: r.name,
      email: r.email,
      birthday: r.birthday || null,
      start_date: r.start_date || null,
      dietary_requirements: r.dietary_requirements
        ? r.dietary_requirements.split(";").map((s) => s.trim()).filter(Boolean)
        : [],
      allergies: r.allergies
        ? r.allergies.split(";").map((s) => s.trim()).filter(Boolean)
        : [],
      department: r.department || null,
      office_location: r.office_location || null,
      data_source: "user_csv" as const,
    }));

    let insertErr: { message: string } | null = null;
    if (inserts.length > 0) {
      const { error } = await supabase.from("team_members").insert(inserts);
      insertErr = error;
    }

    // Updates are per-row because Supabase JS doesn't expose a bulk
    // update primitive. Promise.all keeps the wall-clock cost flat;
    // one failure doesn't abort the others, but the toast surfaces
    // the count of failures so the user can retry.
    const updateResults = await Promise.allSettled(
      analysis.toUpdate.map(async ({ id, existing, incoming, fieldsFilled, overrides }) => {
        const updates: {
          enriched_at: string;
          name?: string;
          birthday?: string | null;
          start_date?: string | null;
          department?: string | null;
          office_location?: string | null;
          dietary_requirements?: string[];
          allergies?: string[];
          data_source?: string;
        } = { enriched_at: new Date().toISOString() };

        // Only patch fields the analysis identified as "fillable", we
        // never overwrite present values from a CSV. The merge step in
        // the dialog already separated conflicts out of this list.
        if (fieldsFilled.includes("name") && incoming.name) updates.name = incoming.name;
        if (fieldsFilled.includes("birthday") && incoming.birthday) updates.birthday = incoming.birthday;
        if (fieldsFilled.includes("start_date") && incoming.start_date) updates.start_date = incoming.start_date;
        if (fieldsFilled.includes("department") && incoming.department) updates.department = incoming.department;
        if (fieldsFilled.includes("office_location") && incoming.office_location) updates.office_location = incoming.office_location;

        if (fieldsFilled.includes("dietary_requirements")) {
          const incomingDiet = incoming.dietary_requirements
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean);
          const merged = Array.from(new Set([...(existing.dietary_requirements ?? []), ...incomingDiet]));
          updates.dietary_requirements = merged;
        }

        if (fieldsFilled.includes("allergies")) {
          const incomingAllergies = incoming.allergies
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean);
          const merged = Array.from(new Set([...(existing.allergies ?? []), ...incomingAllergies]));
          updates.allergies = merged;
        }

        // M7: apply user-chosen conflict overrides on top. These
        // are deliberate "use CSV value" picks for fields that had a
        // conflict (different existing value), so they overwrite the
        // existing value entirely rather than merging. Date fields
        // are stored as ISO strings; everything else is plain text.
        for (const [field, csvValue] of Object.entries(overrides)) {
          if (field === "name") updates.name = csvValue;
          else if (field === "birthday") updates.birthday = csvValue;
          else if (field === "start_date") updates.start_date = csvValue;
          else if (field === "department") updates.department = csvValue;
          else if (field === "office_location") updates.office_location = csvValue;
        }

        // Once a user-driven CSV has touched a row, promote its
        // provenance to user_csv so the missing-data widget can stop
        // flagging it as "needs your confirmation".
        if (existing.data_source === "admin_seeded") {
          updates.data_source = "user_csv";
        }

        return supabase.from("team_members").update(updates).eq("id", id);
      }),
    );

    const updateFailures = updateResults.filter(
      (r) => r.status === "rejected" || (r.status === "fulfilled" && r.value.error),
    ).length;

    if (insertErr || updateFailures > 0) {
      toast.error(
        insertErr
          ? `Failed to import members: ${insertErr.message}`
          : `Imported ${inserts.length} new, but ${updateFailures} update${
              updateFailures !== 1 ? "s" : ""
            } failed. Check the team list and retry as needed.`,
      );
      invalidateTeam();
      return;
    }

    const parts: string[] = [];
    if (inserts.length > 0) parts.push(`${inserts.length} new`);
    if (analysis.toUpdate.length > 0) parts.push(`${analysis.toUpdate.length} merged`);
    if (analysis.conflicts.length > 0) parts.push(`${analysis.conflicts.length} conflict${analysis.conflicts.length !== 1 ? "s" : ""} kept as-is`);
    if (analysis.inCsvDuplicates.length > 0) parts.push(`${analysis.inCsvDuplicates.length} CSV duplicate${analysis.inCsvDuplicates.length !== 1 ? "s" : ""} dropped`);
    toast.success(`Imported: ${parts.join(", ")}.`);
    invalidateTeam();
  };

  const completenessSummary = useMemo(
    () => summariseCompleteness(members.filter((m) => m.is_active)),
    [members],
  );

  const filtered = useMemo(() => {
    let list = members;
    if (filter === "active") list = list.filter((m) => m.is_active);
    if (filter === "inactive") list = list.filter((m) => !m.is_active);
    if (filter === "needs_attention") {
      list = list.filter((m) => m.is_active && getMissingFields(m).hasIssues);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      // MS438: m.email can be null (admin-seeded rows without an email address).
      // Guard with optional chaining to avoid "Cannot read properties of null".
      list = list.filter((m) => m.name.toLowerCase().includes(q) || (m.email?.toLowerCase() ?? "").includes(q));
    }
    if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "birthday") {
      const today = new Date();
      const dayOfYear = (d: Date) => { const start = new Date(d.getFullYear(), 0, 0); return (d.getTime() - start.getTime()) / 86400000; };
      // Members with no birthday on file (admin-seeded but unconfirmed)
      // sink to the bottom rather than crashing the comparator. They
      // still appear in the list, Phase 3's missing-data flags will
      // surface them for the user to fix.
      const SINK = Number.POSITIVE_INFINITY;
      list = [...list].sort((a, b) => {
        const aDiff = a.birthday
          ? (() => {
              // MS442: birthday is a date-only Postgres column. Without "T00:00:00"
              // the constructor parses as UTC midnight → local date is one day behind
              // in AEST. Use T00:00:00 to anchor as local midnight, matching the
              // T00:00:00 suffix used on every display path (see line 657).
              const aDate = new Date(new Date(a.birthday + "T00:00:00").setFullYear(today.getFullYear()));
              return dayOfYear(aDate) >= dayOfYear(today)
                ? dayOfYear(aDate) - dayOfYear(today)
                : 365 + dayOfYear(aDate) - dayOfYear(today);
            })()
          : SINK;
        const bDiff = b.birthday
          ? (() => {
              const bDate = new Date(new Date(b.birthday + "T00:00:00").setFullYear(today.getFullYear()));
              return dayOfYear(bDate) >= dayOfYear(today)
                ? dayOfYear(bDate) - dayOfYear(today)
                : 365 + dayOfYear(bDate) - dayOfYear(today);
            })()
          : SINK;
        return aDiff - bDiff;
      });
    }
    if (sort === "start_date") {
      list = [...list].sort((a, b) => {
        const aTs = a.start_date ? new Date(a.start_date).getTime() : 0;
        const bTs = b.start_date ? new Date(b.start_date).getTime() : 0;
        return bTs - aTs;
      });
    }
    return list;
  }, [members, filter, search, sort]);

  const activeCount = members.filter((m) => m.is_active).length;

  if (loading && members.length === 0) {
    return (
      // Sprint 2.5a /team-fix, aria-live + visible message. The
      // previous loading view was a silent spinner: screen readers
      // got nothing and sighted users with slow connections wondered
      // if anything was happening. Pattern matches the loading
      // states added in B.2 P1-5 across /orders + /orders/:id.
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center justify-center h-64 gap-3"
      >
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"
          aria-hidden
        />
        <p className="text-sm text-charcoal/60">Loading your team…</p>
      </div>
    );
  }

  return (
    // MS253: PageShell adds breathing room (max-width + responsive
    // horizontal padding). Pre-MS253 this page rendered straight
    // into ConciergeLayout's <Outlet /> with zero inset — headline,
    // CTAs, and member cards stuck to the literal viewport edges.
    // Removed the "Roster" eyebrow per Lucky — it didn't add meaning
    // and just gave another stretched-to-edge element to look at.
    <PageShell width="wide">
      {/* MS409: noindex — /team is an authed internal roster page. */}
      <PageMeta
        title="Your Team · Lembra"
        description="Your team's names, birthdays, work anniversaries, and dietary needs."
        path="/team"
        noindex
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <PageHeader
          title="Your Team"
          emphasis="Team"
          subtitle={`${activeCount} active member${activeCount !== 1 ? "s" : ""}, names and dates and dietaries, all in one place.`}
        />
        <div className="flex flex-wrap gap-3 shrink-0">
          <Button variant="outline" onClick={() => setCsvOpen(true)}>
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <Button variant="primary" onClick={() => { setEditMember(null); setAddOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Add team member
          </Button>
        </div>
      </div>

      {membersQuery.isError && (
        <div className="mb-6">
          <ErrorCard
            title="Couldn't load your team"
            onRetry={() => membersQuery.refetch()}
          />
        </div>
      )}

      {members.length === 0 ? (
        // Sprint 2.5a /team-fix, warmer empty state. This view is
        // ONLY ever seen on a fresh account (the moment after
        // onboarding completes). The previous "No team yet." was
        // utility-flat; the post-onboarding handoff is high-impact
        // and deserves a more deliberate beat. Sparkles icon mirrors
        // the onboarding step-3 finish so the journey feels
        // continuous; copy frames the multi-vendor outcome ("not
        // just birthdays, anniversaries, new starters, milestones");
        // CTA is more committal ("Add my first team member") than
        // generic "Add team member".
        <div
          role="region"
          aria-label="Empty team state"
          className="flex flex-col items-center justify-center py-24 text-center"
        >
          <div className="bg-butter/40 p-5 rounded-full mb-6">
            <Sparkles className="h-12 w-12 text-navy" />
          </div>
          <h2
            className="text-2xl font-bold text-navy mb-2"
            style={{ fontFamily: "Newsreader, Georgia, serif", fontStyle: "italic" }}
          >
            Your team starts here.
          </h2>
          <p className="text-charcoal/70 mb-6 max-w-md">
            Add someone with a name and a date. Lembra takes care of the rest: birthdays, anniversaries, new starters, milestones.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => { setEditMember(null); setAddOpen(true); }}
          >
            <Plus className="h-4 w-4 mr-2" /> Add my first team member
          </Button>
          <p className="mt-4 text-xs text-charcoal/55">
            Have a list already? <button
              type="button"
              onClick={() => setCsvOpen(true)}
              className="underline underline-offset-2 hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral rounded"
            >
              Import from a CSV
            </button> instead.
          </p>
        </div>
      ) : (
        <>
          {/* Missing-data widget. Banner-style attention nudge that
              doubles as a one-click filter to "needs attention". Hidden
              when the team is fully complete so a tidy team isn't
              greeted with a permanent zero-state alert. */}
          {completenessSummary.withIssues > 0 && (
            // Sprint 2.5a /team-fix, aria-pressed announces the
            // toggle state. Without it, screen readers read the
            // banner as a static button each time, with no signal
            // that the filter is currently active or not.
            <button
              type="button"
              onClick={() => setFilter(filter === "needs_attention" ? "all" : "needs_attention")}
              aria-pressed={filter === "needs_attention"}
              className={`w-full mb-6 flex items-center gap-3 rounded-xl border p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 ${
                filter === "needs_attention"
                  ? "border-coral bg-coral/10"
                  : "border-coral/30 bg-coral/5 hover:bg-coral/10"
              }`}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-coral/15 shrink-0">
                <AlertTriangle className="h-5 w-5 text-coral" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy">
                  {completenessSummary.withIssues} team member
                  {completenessSummary.withIssues !== 1 ? "s" : ""} need{completenessSummary.withIssues === 1 ? "s" : ""} attention
                </p>
                <p className="text-sm text-charcoal/70">
                  {[
                    completenessSummary.blocking > 0 &&
                      `${completenessSummary.blocking} missing a celebration date`,
                    completenessSummary.pendingConfirmation > 0 &&
                      `${completenessSummary.pendingConfirmation} added by Lembra, please confirm`,
                    completenessSummary.recommendedOnly > 0 &&
                      `${completenessSummary.recommendedOnly} missing dietary or allergy info`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <span className="text-xs font-medium text-coral shrink-0">
                {filter === "needs_attention" ? "Showing" : "Show only these"}
              </span>
            </button>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="pl-9"
              />
            </div>
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="bg-sky/20">
                <TabsTrigger value="all" className="data-[state=active]:bg-navy data-[state=active]:text-white">All</TabsTrigger>
                <TabsTrigger value="active" className="data-[state=active]:bg-navy data-[state=active]:text-white">Active</TabsTrigger>
                <TabsTrigger value="inactive" className="data-[state=active]:bg-navy data-[state=active]:text-white">Inactive</TabsTrigger>
                {completenessSummary.withIssues > 0 && (
                  <TabsTrigger value="needs_attention" className="data-[state=active]:bg-coral data-[state=active]:text-white">
                    Needs attention
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
            <Select value={sort} onValueChange={setSort}>
              {/* Sprint 2.5a /team-fix, aria-label so screen readers
                  announce the control's purpose. Without it, the
                  Select trigger reads as just the current value
                  ("Name A–Z") with no context. */}
              <SelectTrigger className="w-48" aria-label="Sort team members by">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name A–Z</SelectItem>
                <SelectItem value="birthday">Upcoming birthday</SelectItem>
                <SelectItem value="start_date">Start date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sprint 2.5a /team-fix, region role + aria-label so
              screen readers announce the list and the current
              filtered count. The label is dynamic so a "needs
              attention" filter announces itself rather than just
              "team members list". */}
          <div
            role="region"
            aria-label={
              filter === "needs_attention"
                ? `${filtered.length} team member${filtered.length === 1 ? "" : "s"} needing attention`
                : `${filtered.length} team member${filtered.length === 1 ? "" : "s"}`
            }
            // MS253: bumped gap from gap-4 (16px) to gap-5 sm:gap-6
            // (20/24px) so cards have breathing room between them
            // instead of "congested, tight together" (Lucky's words).
            className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {/* MS401: when members exist but search/filter yields nothing,
                show an inline empty state rather than blank grid space. */}
            {filtered.length === 0 && (
              <div className="col-span-1 sm:col-span-2 lg:col-span-3 py-14 flex flex-col items-center text-center gap-3">
                <p className="text-charcoal/50 text-sm">
                  No team members match {search.trim() ? `"${search.trim()}"` : "this filter"}.
                </p>
                {search.trim() && (
                  <button
                    onClick={() => setSearch("")}
                    className="text-sm text-coral hover:underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
            {filtered.map((m) => {
              const missing = getMissingFields(m);
              // Coloured ring on cards that need attention. Coral for
              // hard-missing celebration dates, butter for everything
              // else (recommendation level). Picking the strongest
              // signal so users see the biggest issue first.
              const ringClass = missing.hasBlockingIssues
                ? "border-coral/50 hover:border-coral"
                : missing.hasIssues
                ? "border-butter hover:border-butter/80 ring-1 ring-butter/40"
                : "border-sky/30 hover:border-navy/30";
              const highlightClass =
                highlightedMemberId === m.id
                  ? "ring-2 ring-coral shadow-md shadow-coral/20"
                  : "";
              return (
              <div
                key={m.id}
                data-testid={`team-member-card-${m.id}`}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/team/${m.id}/profile`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/team/${m.id}/profile`);
                  }
                }}
                className={`text-left bg-sky/15 rounded-xl p-5 border ${ringClass} ${highlightClass} hover:shadow-md transition-all duration-200 cursor-pointer ${!m.is_active ? "opacity-50" : ""}`}
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <h3 className="font-bold text-navy text-lg flex-1 min-w-0 truncate">{m.name}</h3>
                  <div className="flex items-center gap-1 shrink-0">
                    {!m.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                    {m.is_active && missing.hasBlockingIssues && (
                      <Badge className="bg-coral text-white hover:bg-coral text-[10px] uppercase tracking-wide">
                        Missing info
                      </Badge>
                    )}
                    {m.is_active && !missing.hasBlockingIssues && missing.needsAdminSeedConfirmation && (
                      <Badge className="bg-butter text-navy hover:bg-butter text-[10px] uppercase tracking-wide">
                        Confirm
                      </Badge>
                    )}
                    {/* Sprint 1.3, autopilot-eligible badge. Renders only on
                        active members not flagged with the more urgent
                        Missing-info / Confirm badges (those take precedence
                        because they're calls to action).
                        MS213: native-title tooltip explains scope — the
                        cron is cake-only today (see MS212 + scheduled-
                        auto-approve-orders), so the badge promising
                        "Autopilot" without qualification was misleading
                        for buyers who'd ordered hampers/flowers/spa for
                        the same person and expected those to auto-fire
                        too. The badge shows the per-member READINESS
                        flag; whether autopilot actually fires also
                        depends on the company-level toggle (Settings →
                        Concierge autopilot). */}
                    {m.is_active && !missing.hasBlockingIssues && !missing.needsAdminSeedConfirmation && m.auto_eligible && (
                      <Badge
                        className="bg-navy text-butter hover:bg-navy text-[10px] uppercase tracking-wide cursor-help"
                        title="Cake orders for this person can auto-fire when Concierge autopilot is on (Settings → Concierge autopilot). Other categories still need your approval each time."
                      >
                        Autopilot-ready
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-charcoal/70 text-sm mb-3">{m.email}</p>
                <div className="grid grid-cols-2 gap-y-1 text-sm mb-3">
                  <span className="text-charcoal/50">Birthday</span>
                  <span className={`font-medium ${m.birthday ? "text-navy" : "text-charcoal/40"}`}>
                    {m.birthday ? format(new Date(m.birthday + "T00:00:00"), "d MMMM") : "Not set"}
                  </span>
                  <span className="text-charcoal/50">Started</span>
                  <span className={`font-medium ${m.start_date ? "text-navy" : "text-charcoal/40"}`}>
                    {m.start_date ? format(new Date(m.start_date + "T00:00:00"), "d MMM yyyy") : "Not set"}
                  </span>
                  {m.department && (
                    <>
                      <span className="text-charcoal/50">Department</span>
                      <span className="text-navy font-medium">{m.department}</span>
                    </>
                  )}
                </div>
                {/* Allergies first, treated as a coral medical badge.
                    Visual hierarchy is intentional: a peanut allergy
                    matters more than a vegan preference and the eye
                    should see it first. */}
                {m.allergies && m.allergies.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 items-center mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-coral">Allergies</span>
                    {m.allergies.map((a) => (
                      <Badge
                        key={a}
                        className="text-xs bg-coral/15 text-coral hover:bg-coral/15 border border-coral/30"
                      >
                        {a}
                      </Badge>
                    ))}
                  </div>
                )}

                {m.dietary_requirements && m.dietary_requirements.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {m.dietary_requirements.map((d) => (
                      <Badge key={d} variant="sky" className="text-xs">{d}</Badge>
                    ))}
                  </div>
                )}

                {/* Missing-fields chip strip. Sits below dietary so cards
                    keep a stable shape, present fields up top, gaps at
                    the bottom. Pre-seed-confirmation message gets its
                    own chip so the user knows it's "please review",
                    not "you forgot something". */}
                {m.is_active && missing.hasIssues && (
                  <div className="mt-3 pt-3 border-t border-charcoal/10 space-y-1.5">
                    {missing.needsAdminSeedConfirmation && missing.required.length === 0 && (
                      <p className="text-[11px] text-charcoal/60 flex items-center gap-1">
                        <Sparkles className="h-3 w-3 text-coral" />
                        We filled these in, please check.
                      </p>
                    )}
                    {(missing.required.length > 0 || missing.recommended.length > 0) && (
                      <div className="flex flex-wrap gap-1">
                        {/* MS-cohesion: these chips name the specific gaps
                            the header "Missing info" badge + coral card ring
                            already flag. Kept neutral (charcoal) so a single
                            card shows ONE coral attention signal, not three
                            stacked alarms for the same gap. */}
                        {missing.required.map((f) => (
                          <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full bg-charcoal/8 text-charcoal/70 font-medium">
                            Missing {fieldLabel(f).toLowerCase()}
                          </span>
                        ))}
                        {missing.recommended.map((f) => (
                          <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full bg-butter/60 text-navy font-medium">
                            No {fieldLabel(f).toLowerCase()} set
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* MS64c: per-card affordances row. Send a gift →
                    deep-links into /chat with recipient_name +
                    recipient_id pre-loaded so chat-concierge's
                    get_team_member fires on first turn with the
                    right profile. Full profile → the rich
                    enrichment page (Sprint 1.2). stopPropagation
                    on both so clicking doesn't also fire the
                    tile's edit dialog. */}
                <div className="mt-3 pt-3 border-t border-charcoal/10 flex items-center justify-between gap-2">
                  <Link
                    to={`/chat?recipient=${encodeURIComponent(m.name)}&recipientId=${encodeURIComponent(m.id)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-full bg-coral px-2.5 py-1 text-xs font-semibold text-white hover:bg-coral/85 transition-colors"
                    aria-label={`Find a gift for ${m.name}`}
                  >
                    Find a gift
                    <ChevronRight className="h-3 w-3" />
                  </Link>
                  <Link
                    to={`/team/${m.id}/profile`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-coral hover:text-coral/70"
                  >
                    Full profile
                    <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}

      <AddTeamMemberDialog
        open={addOpen}
        onOpenChange={(v) => { setAddOpen(v); if (!v) setEditMember(null); }}
        onSave={handleSave}
        companyId={companyId || undefined}
        member={editMember || undefined}
        onDeactivate={handleDeactivate}
      />
      <ImportCsvDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        onImport={handleCsvImport}
        existingMembers={existingMembersLite}
      />
    </PageShell>
  );
}
